/** Định dạng dùng chung cho các màn hình. */

/**
 * Kiểu khai ở `packages/shared` nói là `Date`, nhưng **lúc chạy thì đó là CHUỖI**.
 *
 * `z.coerce.date()` chỉ đổi kiểu khi có ai đó gọi `.parse()`; ở frontend thì
 * `api.ts` trả thẳng `res.json()` mà không parse, nên mọi trường ngày đều là chuỗi
 * ISO đội lốt `Date`. TypeScript không bắt được, và triệu chứng là một màn hình
 * trắng kèm `x.getTime is not a function` — đã xảy ra thật ở spec SC-08.
 *
 * Vì vậy: **KHÔNG BAO GIỜ gọi thẳng phương thức của `Date`** lên dữ liệu từ API.
 * Đi qua `toDate` hoặc một trong các hàm dưới đây.
 */
type DateLike = Date | string | null;

/** Ép về `Date` thật để gọi được `.getTime()`, `.toLocaleString()`… */
export function toDate(d: Date | string): Date {
  return d instanceof Date ? d : new Date(d);
}

export function formatDateTime(d: DateLike): string {
  if (!d) return '—';
  return new Date(d).toLocaleString('vi-VN', { dateStyle: 'short', timeStyle: 'short' });
}

/**
 * Thời gian tương đối ("3 phút trước"). Bảng Registry thường có cả loạt trang
 * quét cùng lúc, mốc giờ tuyệt đối giống nhau y hệt nên mắt không đọc được gì;
 * điều người dùng cần biết là dữ liệu còn mới hay đã cũ. Mốc đầy đủ để ở tooltip.
 */
export function formatRelative(d: DateLike): string {
  if (!d) return 'chưa quét';
  const ms = Date.now() - new Date(d).getTime();
  const min = Math.round(ms / 60000);
  if (min < 1) return 'vừa xong';
  if (min < 60) return `${min} phút trước`;
  const hour = Math.round(min / 60);
  if (hour < 24) return `${hour} giờ trước`;
  const day = Math.round(hour / 24);
  if (day < 30) return `${day} ngày trước`;
  return new Date(d).toLocaleDateString('vi-VN', { dateStyle: 'short' });
}

/** Dữ liệu quét quá một tuần thì coi là cũ — tô nhạt để người dùng biết cần quét lại. */
export function isStale(d: DateLike): boolean {
  if (!d) return true;
  const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
  return Date.now() - new Date(d).getTime() > SEVEN_DAYS_MS;
}

/** Màu ô chữ viết tắt của sản phẩm — suy ra từ slug để mỗi trang có một màu ổn định. */
const PROD_COLORS = ['#0E7C7B', '#2EC4B6', '#F2A65A', '#0F3A57', '#D1495B', '#5F7683'] as const;
export function prodColor(slug: string): string {
  let h = 0;
  for (const ch of slug) h = (h * 31 + (ch.codePointAt(0) ?? 0)) % 997;
  return PROD_COLORS[h % PROD_COLORS.length] ?? PROD_COLORS[0];
}

/** Hai chữ đầu của slug, dùng cho ô màu vuông trong bảng. */
export function prodInitials(code: string): string {
  const parts = code.split(/[-_\s]/).filter(Boolean);
  const two = parts.length > 1 ? `${parts[0]?.[0] ?? ''}${parts[1]?.[0] ?? ''}` : code.slice(0, 2);
  return two.toUpperCase();
}
